import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    CalendarClock, Flag, Hourglass, AlertTriangle, Route as RouteIcon,
} from 'lucide-react';
import {
    progressLabel, progressPercent, dueInLabel, provenanceLine, programmeStops,
} from '../../utils/careProgrammes';
import { formatDay } from '../health/healthFormat';
import ProgrammeStopLine, { StopStateChip } from './ProgrammeStopLine';

// ONE PATIENT PROGRAMME, AS THE SERVER COMPUTED IT (contract §1 "Timeline
// object"; doc/programme-visits.md). Shared by the Programme card (doctor /
// admin), the desk strip (staff: timeline projection — no notes) and the
// health record's read-only Programmes section. Nothing here is recomputed:
// week, expected end, progress and every stop's state are the server's
// answer, printed — the stops as THE STOP LINE (ProgrammeStopLine), the one
// look the booking question draws too.
//
// `milestoneActions(stop)` lets the host add buttons to a stop (tick /
// untick); `headerActions` sits beside the name (Close).

/** Kept for callers that import it: the stop line's state chip. */
export const StateChip = StopStateChip;

/** Where the run is against its expected end: "Expected end 13 Jan 2027 · 109 days to go". */
const endLine = (timeline, t) => {
    if (!timeline?.expectedEnd) return t('programmes.open_ended_run');
    const date = formatDay(timeline.expectedEnd);
    const n = Number(timeline.daysToEnd);
    let tail = '';
    if (Number.isFinite(n)) {
        if (n > 0) tail = t('programmes.days_to_go', { n });
        else if (n === 0) tail = t('programmes.ends_today');
        else tail = t('programmes.past_end', { n: -n });
    }
    return tail ? `${t('programmes.expected_end', { date })} · ${tail}` : t('programmes.expected_end', { date });
};

function ProgrammeTimeline({ programme, scope = 'full', headerActions = null, milestoneActions = null, compact = false }) {
    const { t } = useTranslation();
    const p = programme || {};
    const tl = p.timeline || {};
    const id = p.patientProgrammeId;
    const closed = p.status === 'closed';
    const pct = progressPercent(tl);
    const stops = programmeStops(p);
    const counts = tl.counts || {};
    const provenance = provenanceLine(scope === 'timeline' ? { doctorName: p.doctorName } : p, t);

    return (
        <article data-testid={`programme-run-${id}`} className={`rounded-2xl border bg-white p-4 shadow-sm ${closed ? 'border-gray-100' : 'border-[#4ABDBD]/25'}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                    <h4 data-testid={`programme-run-name-${id}`} className="flex items-center gap-1.5 text-sm font-bold text-gray-800">
                        <RouteIcon className="h-4 w-4 shrink-0 text-[#4ABDBD]" aria-hidden="true" />
                        <span className="truncate">{p.name}</span>
                        {closed && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">{t('programmes.status_closed')}</span>
                        )}
                    </h4>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                        {t('programmes.start_date_line', { label: p.startLabel || t('programmes.start_date'), date: formatDay(p.startDate) })}
                        {provenance ? ` · ${provenance}` : ''}
                    </p>
                </div>
                {headerActions ? <div className="flex shrink-0 flex-wrap items-center gap-1.5">{headerActions}</div> : null}
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-[auto_1fr] sm:items-center">
                <span data-testid={`programme-run-week-${id}`} className="inline-flex w-fit items-center gap-1.5 rounded-xl bg-[#4ABDBD]/10 px-3 py-1.5 text-sm font-bold text-[#2D8A8A]">
                    <CalendarClock className="h-4 w-4" aria-hidden="true" />
                    {progressLabel(tl, t)}
                </span>
                <div className="min-w-0">
                    <p data-testid={`programme-run-end-${id}`} className="text-xs text-gray-600">{endLine(tl, t)}</p>
                    {pct !== null && (
                        <div
                            className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
                            role="progressbar"
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={pct}
                            aria-label={t('programmes.progress')}
                        >
                            <div className="h-full rounded-full bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A]" style={{ width: `${pct}%` }} />
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-semibold text-gray-600">
                    <Flag className="h-3 w-3" aria-hidden="true" />
                    {t('programmes.done_count', { done: counts.done || 0, total: counts.total || stops.filter((x) => x.kind === 'stop').length })}
                </span>
                {tl.next && (
                    <span data-testid={`programme-run-next-${id}`} className="inline-flex items-center gap-1 rounded-full bg-[#D7A05C]/15 px-2 py-0.5 font-semibold text-[#9a6a2c]">
                        <Hourglass className="h-3 w-3" aria-hidden="true" />
                        {t('programmes.next_line', { name: tl.next.name, when: dueInLabel(tl.next, t) })}
                    </span>
                )}
                {(tl.late || []).map((m) => (
                    <span key={m.key} data-testid={`programme-run-late-${id}-${m.key}`} className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-semibold text-red-600">
                        <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                        {t('programmes.late_chip', { name: m.name })}
                    </span>
                ))}
            </div>

            {closed && (
                <div data-testid={`programme-run-outcome-${id}`} className="mt-3 rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-700">
                    <p><span className="font-semibold">{t('programmes.outcome_label')}:</span> {p.outcome}</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">
                        {t('programmes.closed_on', { date: formatDay(p.closedAt) })}
                        {p.lifeEventEntryId ? ` · ${t('programmes.life_event_added')}` : ''}
                    </p>
                </div>
            )}

            {!compact && (
                <div data-testid={`programme-run-milestones-${id}`} className="mt-3">
                    <ProgrammeStopLine
                        stops={stops}
                        testIdPrefix={(part, key) => `programme-milestone-${part}-${id}-${key}`}
                        actions={closed ? null : milestoneActions}
                        showNotes={scope !== 'timeline'}
                    />
                </div>
            )}
        </article>
    );
}

export default ProgrammeTimeline;
